import { useCallback, useMemo } from "react";

import { getExistingStatusValue, isAnyValueStatus } from "~/tools/request-status";

import type { TimeRange } from "../../../../../../app/state/view/timeline/TimeRange";
import { getEventsObservableWithNextCursor } from "../../../../../../backend/EventsApi";
import type { GetEventsParams, GetEventsSuccess, TopologyEvent } from "../../../../../../common/openapi";
import { useTimeTravelEvents } from "../../../../event-perspective/TimeTravelEventsHook";
import { useEventPerspectiveProps } from "../../../../event-perspective/useEventPerspectiveProps";

const EVENTS_LIMIT = 10;

const getEventsFromResponses = (value: Array<GetEventsSuccess>): Array<TopologyEvent> =>
	value.flatMap(({ items }) => items);

export interface UsePollingEventsHookProps {
	topologyQuery?: string;
	eventsTimeRange?: TimeRange;
	eventsLimit?: number;
	eventType?: string;
}

export const usePollingEventsHook = ({
	topologyQuery,
	eventsTimeRange,
	eventsLimit,
	eventType,
}: UsePollingEventsHookProps) => {
	const {
		selectedTimestamp,
		timeRange: currentTimeRange,
		topologyQuery: viewTopologyQuery,
		eventTypes,
		eventTags,
		eventCategories,
		eventSources,
	} = useEventPerspectiveProps();

	const timeRange = eventsTimeRange ?? currentTimeRange;

	const getEventsParams: GetEventsParams = useMemo(
		() => ({
			body: {
				limit: eventsLimit ?? EVENTS_LIMIT,
				topologyQuery: topologyQuery ?? viewTopologyQuery,
				startTimestampMs: timeRange.start().getTime(),
				endTimestampMs: timeRange.endDate().getTime(),
				playHeadTimestampMs: selectedTimestamp,
				eventTypes: eventType !== undefined ? [eventType] : eventTypes,
				eventTags,
				eventCategories,
				eventSources,
				cursor: undefined,
			},
		}),
		[
			timeRange,
			topologyQuery,
			viewTopologyQuery,
			selectedTimestamp,
			eventTypes,
			eventTags,
			eventCategories,
			eventSources,
			eventType,
			eventsLimit,
		],
	);

	const requestEvents = useCallback((params: GetEventsParams) => getEventsObservableWithNextCursor(params), []);

	const { status, onRestartRequest } = useTimeTravelEvents({
		timeTravelParams: getEventsParams,
		requestEvents,
	});

	const value = isAnyValueStatus(status) ? getExistingStatusValue(status) : [];
	const total = value.at(0)?.total ?? 0;
	const data = getEventsFromResponses(value);

	return {
		status,
		onRestartRequest,
		total,
		data,
	};
};
